// app.component.ts (or an AuthService you call on startup)
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  InteractionRequiredAuthError,
  SilentRequest,
  SsoSilentRequest,
  AccountInfo,
  BrowserAuthError,
} from '@azure/msal-browser';
import { AppInitService } from './services/app-init.service';
import { LoadingService } from './services/loading.service';
import { LoadingSpinnerComponent } from './components/shared/loading-spinner/loading-spinner.component';
import { environment } from '../environments/environment';
import { LoggerService } from './services/logger.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, LoadingSpinnerComponent],
  templateUrl: './app.html',
})
export class App implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly msal = inject(MsalService);
  private readonly appInitService = inject(AppInitService);
  private readonly loadingService = inject(LoadingService);
  private readonly logger = inject(LoggerService);

  ngOnInit(): void {
    // Show loading spinner during initialization
    this.loadingService.show('Loading...');

    // 1) Always handle any pending redirect and then restore
    this.msal.handleRedirectObservable().subscribe({
      next: async (result) => {
        if (result?.account) {
          this.msal.instance.setActiveAccount(result.account);
          // Save loginHint for future ssoSilent
          localStorage.setItem('loginHint', result.account.username || '');
          // Store authority from redirect result for logout consistency
          if (result.authority) {
            localStorage.setItem('msal.login.authority', result.authority);
            this.logger.info(
              '[Auth] Stored authority from redirect result:',
              result.authority,
            );
          }

          this.logger.info(
            '[Auth] Login redirect completed successfully for account:',
            result.account.username,
          );

          // Signal initialization complete
          this.appInitService.setInitialized();
          this.loadingService.hide();
          // Let AuthService handle post-login navigation after org check
          return;
        }

        // Check if we have a loginHint - if not, user likely logged out
        const loginHint = localStorage.getItem('loginHint');
        if (!loginHint) {
          // No loginHint means logout happened - don't restore accounts
          this.appInitService.setInitialized();
          this.loadingService.hide();
          return;
        }

        // Always restore from cache first
        this.restoreActiveAccountFromCache();

        // Only try silent auth if we don't have an account after restoring
        const hasAccount = this.msal.instance.getAllAccounts().length > 0;
        if (!hasAccount) {
          await this.trySilentAuth();
        }

        // Signal initialization complete
        this.appInitService.setInitialized();
        this.loadingService.hide();
      },
      error: async () => {
        // Check if we have a loginHint - if not, user likely logged out
        const loginHint = localStorage.getItem('loginHint');
        if (!loginHint) {
          // No loginHint means logout happened - don't restore accounts
          this.appInitService.setInitialized();
          this.loadingService.hide();
          return;
        }

        this.restoreActiveAccountFromCache();

        // Only try silent auth if we don't have an account after restoring
        const hasAccount = this.msal.instance.getAllAccounts().length > 0;
        if (!hasAccount) {
          await this.trySilentAuth();
        }

        // Signal initialization complete even on error
        this.appInitService.setInitialized();
        this.loadingService.hide();
      },
    });
  }

  private restoreActiveAccountFromCache() {
    const active = this.msal.instance.getActiveAccount();
    if (active) return;

    const accounts = this.msal.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msal.instance.setActiveAccount(accounts[0]);
      // Keep a loginHint for ssoSilent fallback
      localStorage.setItem('loginHint', accounts[0].username || '');
    }
  }

  /**
   * Attempt silent token first; if blocked by 3P cookies, try ssoSilent(loginHint).
   * If both fail with interaction_required, fall back to loginRedirect.
   */
  private async trySilentAuth() {
    const account = this.msal.instance.getActiveAccount();

    // If we still don't have an account, try ssoSilent directly with a stored loginHint
    if (!account) {
      const hint = localStorage.getItem('loginHint') || '';
      if (hint) {
        try {
          await this.msal.instance.ssoSilent(this.buildSsoSilentRequest(hint));
          const refreshed = this.msal.instance.getAllAccounts()[0];
          if (refreshed) this.msal.instance.setActiveAccount(refreshed);
          return;
        } catch (e) {
          this.handleSilentFailure(e);
          return;
        }
      }
      // No hint → interactive
      this.msal.loginRedirect();
      return;
    }

    // We have an account: prefer acquireTokenSilent (refresh path)
    try {
      await this.msal.instance.acquireTokenSilent(
        this.buildSilentRequest(account),
      );
      return; // success
    } catch (e) {
      // Try ssoSilent with loginHint as a second chance
      const hint = account.username || localStorage.getItem('loginHint') || '';
      if (hint) {
        try {
          await this.msal.instance.ssoSilent(this.buildSsoSilentRequest(hint));
          // setActiveAccount is automatic on success via cache, but ensure it:
          const refreshed = this.msal.instance.getAllAccounts()[0];
          if (refreshed) this.msal.instance.setActiveAccount(refreshed);
          return;
        } catch (e2) {
          this.handleSilentFailure(e2);
          return;
        }
      }
      this.handleSilentFailure(e);
    }
  }

  private buildSilentRequest(account: AccountInfo): SilentRequest {
    // Retrieve stored authority for silent token requests
    const storedAuthority = localStorage.getItem('msal.login.authority');

    const request: SilentRequest = {
      account,
      scopes: environment.apiConfig.scopes,
      forceRefresh: false,
    };

    // Include authority if we have it stored
    if (storedAuthority) {
      request.authority = storedAuthority;
    }

    return request;
  }

  private buildSsoSilentRequest(loginHint: string): SsoSilentRequest {
    // Retrieve stored authority for SSO silent requests
    const storedAuthority = localStorage.getItem('msal.login.authority');

    const request: SsoSilentRequest = {
      loginHint,
      scopes: environment.apiConfig.scopes,
    };

    // Include authority if we have it stored
    if (storedAuthority) {
      request.authority = storedAuthority;
    }

    return request;
  }

  private handleSilentFailure(e: unknown) {
    // If 3rd-party cookies are blocked or AAD session missing, you'll see "interaction_required".
    if (e instanceof InteractionRequiredAuthError) {
      this.msal.loginRedirect();
      return;
    }
    // Some browsers throw BrowserAuthError for blocked iframes/cookies
    if (e instanceof BrowserAuthError) {
      this.msal.loginRedirect();
      return;
    }
    // Default: try interactive to recover
    this.msal.loginRedirect();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
